import type { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { IDENTITY_TOKEN_HEADER, isElevationRequired } from '@app/interceptor';
import type { IdentityService } from '@app/interface/identity/identity-service.interface';
import { WINDOW } from '@app/core/window.token';

@Injectable()
export class IdentityInterceptor implements HttpInterceptor {
  private identityService: IdentityService = inject(angularjs.IdentityService);
  private readonly window = inject(WINDOW);
  constructor() {}

  private getCurrentUser(): unknown {
    return this.window.$sflyAuth?.currentUser;
  }

  private getJwt(): Promise<string | null> {
    if (!this.window.$sflyAuth?.getIdToken) {
      return Promise.resolve(null);
    }
    return this.window.$sflyAuth
      .getIdToken()
      .then((data) => data.jwtToken)
      .catch(() => null);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.getCurrentUser()) {
      return next.handle(req).pipe(catchError((error) => this.handleError(error)));
    }

    return from(this.getJwt()).pipe(
      switchMap((jwt) => {
        const clonedReq = jwt ? req.clone({ setHeaders: { [IDENTITY_TOKEN_HEADER]: jwt } }) : req;
        return next.handle(clonedReq);
      }),
      catchError((error) => this.handleError(error))
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (isElevationRequired(error.error)) {
      this.identityService.goToElevation();
    }
    return throwError(() => error);
  }
}
